import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  ArrowRight, ArrowUpRight, Globe, Star, Zap, UserPlus, CreditCard, Gift, Rocket,
  Sparkles, ShieldCheck, TrendingUp, Users,
} from 'lucide-react';
import { SectionReveal, Stars } from './motion';

const STATIC_FEATURES = [
  {
    icon: Globe,
    label: '01 · Access',
    title: 'Unlimited Access',
    desc: 'A growing gateway to services designed to make access easier and more rewarding.',
    accent: '#00E5FF',
  },
  {
    icon: Star,
    label: '02 · Savings',
    title: 'Exclusive Discounts',
    desc: 'Unlock curated product deals and member-focused savings across the marketplace.',
    accent: '#FFB800',
  },
  {
    icon: Zap,
    label: '03 · Momentum',
    title: 'Endless Opportunities',
    desc: 'A platform built around possibility, connection, and forward momentum.',
    accent: '#007AFF',
  },
];

const STATIC_STEPS = [
  { color: '#00E5FF', icon: UserPlus, title: 'Create Account', desc: 'Sign up with your ID verification. Quick, secure, and ready in minutes.' },
  { color: '#FFB800', icon: CreditCard, title: 'Choose a Plan', desc: 'Pick the membership tier that fits you. Pay securely via GCash, card, or Maya.' },
  { color: '#00E676', icon: Gift, title: 'Access Services', desc: 'Browse exclusive deals, discounted services, and member-only perks instantly.' },
  { color: '#7DD3FC', icon: Rocket, title: 'Earn & Grow', desc: 'Refer friends, earn multi-level commissions, and watch your wallet grow.' },
];

const TICKER = [
  { icon: ShieldCheck, text: 'Bank-grade identity verification' },
  { icon: Sparkles, text: 'Curated merchant discounts' },
  { icon: TrendingUp, text: 'Multi-level referral rewards' },
  { icon: Users, text: 'Trusted community of members' },
  { icon: Globe, text: 'A single membership · unlimited doors' },
];

const PROOF_STATS = [
  { value: '2-Level', label: 'Referral rewards' },
  { value: '10-100%', label: 'Member-only discounts' },
  { value: '365d', label: 'Persistent sign-in' },
  { value: '24/7', label: 'Ledger transparency' },
];

/**
 * Static fallback used when no CMS `home` page is published. Editorial,
 * asymmetric layout — a luxury take on the AGA aesthetic (deep navy +
 * cyan glass + subtle gold). Receives `onCta` so the coordinator can route
 * the user appropriately.
 */
export function StaticLandingFallback({ onCta }) {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {/* ─────────── HERO ─────────── */}
      <section className="relative overflow-hidden aga-grain pt-4 sm:pt-6">
        <div className="aga-aurora"><span /></div>
        <Stars count={14} />

        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-20 sm:pb-28">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: editorial copy — takes 7/12 on desktop for asymmetric feel */}
            <div className="lg:col-span-7">
              <div className="rise rise-1">
                <span className="eyebrow">A New Kind Of Membership</span>
              </div>

              <h1
                className="rise rise-2 font-outfit font-semibold tracking-tight text-white mt-4 sm:mt-6 leading-[0.95] text-[44px] sm:text-6xl lg:text-[86px] break-words"
                data-testid="hero-title"
              >
                Unlimited access.<br />
                <span className="text-luxe">Endless opportunity.</span>
              </h1>

              <p className="rise rise-3 mt-6 text-[15px] sm:text-lg text-[#A0B4CC] leading-relaxed max-w-xl">
                All Global Access is a single membership that opens doors — to curated services,
                member-priced deals, and a referral economy designed to reward the people who
                grow with us. Elegant. Transparent. Yours.
              </p>

              <div className="rise rise-4 mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  onClick={onCta}
                  className="btn-primary text-base px-7 py-3 h-auto w-full sm:w-auto group"
                  data-testid="hero-cta-btn"
                >
                  Get Started
                  <ArrowUpRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
                <Link to="/marketplace" className="w-full sm:w-auto">
                  <Button
                    variant="outline"
                    className="border-white/20 hover:border-gold text-white hover:bg-white/[0.03] text-base px-7 py-3 h-auto w-full sm:w-auto transition-all"
                    data-testid="hero-explore-btn"
                  >
                    Explore Marketplace
                  </Button>
                </Link>
              </div>

              {/* Proof stats row */}
              <div className="rise rise-5 mt-10 sm:mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 border-t border-white/10 pt-6 sm:pt-8">
                {PROOF_STATS.map((s) => (
                  <div key={s.label} className="flex flex-col">
                    <span className="font-outfit text-2xl sm:text-3xl font-semibold text-white">{s.value}</span>
                    <span className="text-[11px] sm:text-xs text-[#5C7599] uppercase tracking-wider mt-1">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: floating cluster — glass card + medallion */}
            <div className="lg:col-span-5 relative min-h-[400px] hidden lg:block">
              <div
                className="keep-dark absolute top-0 right-0 w-full h-[520px] rounded-[32px] border border-white/10 overflow-hidden rise rise-3"
                style={{
                  background: 'radial-gradient(120% 100% at 100% 0%, rgba(0,229,255,0.18), transparent 55%), linear-gradient(180deg, #041A3B 0%, #000B18 100%)',
                  transform: `translateY(${Math.min(scrollY * 0.05, 30)}px)`,
                }}
              >
                {/* corner mono label */}
                <div className="absolute top-6 left-6 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
                  <span className="font-mono text-[10px] tracking-[0.3em] text-[#5C7599] uppercase">AGA · Live</span>
                </div>
                <div className="absolute top-6 right-6 font-mono text-[10px] tracking-[0.3em] text-[#5C7599] uppercase">M / 2026</div>

                {/* central medallion */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-72 h-72">
                    <div className="absolute inset-0 rounded-full border border-[#00E5FF]/30 animate-[pulse-glow_3s_ease-in-out_infinite]" />
                    <div className="absolute inset-4 rounded-full border border-[#FFB800]/20" />
                    <div className="absolute inset-10 rounded-full border border-white/5" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="font-outfit text-6xl font-bold text-luxe leading-none">∞</div>
                        <div className="mt-3 font-mono text-[10px] tracking-[0.3em] text-[#A0B4CC] uppercase">All Access</div>
                      </div>
                    </div>
                    {/* orbiting dots */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{ animation: 'spin 30s linear infinite' }}
                    >
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 rounded-full bg-[#00E5FF]" />
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 -mb-1 w-2 h-2 rounded-full bg-[#FFB800]" />
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -ml-1 w-1.5 h-1.5 rounded-full bg-[#00E676]" />
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 -mr-1 w-1.5 h-1.5 rounded-full bg-[#7DD3FC]" />
                    </div>
                  </div>
                </div>

                {/* bottom footer stripe */}
                <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 px-6 py-4 flex items-center justify-between font-mono text-[10px] tracking-[0.25em] uppercase text-[#5C7599]">
                  <span>Membership · Referrals · Rewards</span>
                  <ArrowRight className="w-3 h-3 text-[#00E5FF]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ticker strip */}
        <div className="relative z-[2] border-y border-white/[0.06] bg-[#000B18]/60 backdrop-blur-sm py-4 overflow-hidden">
          <div className="marquee whitespace-nowrap">
            {[...TICKER, ...TICKER].map((t, i) => (
              <span
                key={`ticker-${i}`}
                className="inline-flex items-center gap-2 text-[13px] sm:text-sm text-[#A0B4CC]"
              >
                <t.icon className="w-3.5 h-3.5 text-[#FFB800]" />
                {t.text}
                <span className="text-[#5C7599]/40">◈</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── FEATURES — Editorial split cards ─────────── */}
      <section className="relative py-16 sm:py-24 md:py-32 aga-grain">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-12 sm:mb-16 items-end">
            <div className="lg:col-span-7">
              <span className="eyebrow">The Difference</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-semibold text-white mt-4 leading-[1.05]">
                Not another loyalty card.<br />
                A membership that <span className="text-luxe">actually rewards you</span>.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-white/10">
              <p className="text-[#A0B4CC] text-[15px] sm:text-base leading-relaxed">
                Every AGA member gets access, savings, and a real stake in the network they build.
                Refer a friend and earn commissions. Grow the network, grow your certificate.
              </p>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {STATIC_FEATURES.map((f, i) => (
              <Card
                key={f.title}
                className="tilt-card group relative overflow-hidden rounded-2xl bg-[#041A3B]/60 backdrop-blur border-white/[0.08] hover:border-white/20 transition-all duration-500 h-full"
                style={{ animationDelay: `${0.1 + i * 0.15}s` }}
              >
                {/* accent stripe */}
                <div
                  className="absolute left-0 top-0 h-full w-[3px] transition-all duration-500 group-hover:w-[5px]"
                  style={{ backgroundColor: f.accent }}
                />
                <CardContent className="p-7 sm:p-8">
                  <div className="flex items-start justify-between mb-8">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:-rotate-6"
                      style={{ backgroundColor: `${f.accent}18`, border: `1px solid ${f.accent}30` }}
                    >
                      <f.icon className="w-5 h-5" style={{ color: f.accent }} />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.25em] text-[#5C7599] uppercase">
                      {f.label}
                    </span>
                  </div>
                  <h3 className="font-outfit text-2xl font-semibold text-white mb-3 leading-tight">
                    {f.title}
                  </h3>
                  <p className="text-[#A0B4CC] text-sm leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── JOURNEY — steps ─────────── */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden">
        <div className="aga-aurora opacity-40"><span /></div>
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14 sm:mb-20">
            <span className="eyebrow">Four Steps</span>
            <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-semibold text-white mt-4 leading-[1.05] break-words" data-testid="how-it-works-title">
              From sign-up to <span className="text-luxe">rewards</span>.
            </h2>
            <p className="mt-5 text-[#A0B4CC] text-sm sm:text-base">
              A single flow to unlock a world of access, savings, and earnings.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {STATIC_STEPS.map((s, i) => (
              <div
                key={s.title}
                className="group relative"
                data-testid={`step-${i + 1}`}
                style={{ transform: `translateY(${[0, 12, 4, 16][i]}px)` }}
              >
                <div className="keep-dark relative p-6 sm:p-7 rounded-2xl bg-gradient-to-b from-[#051f45] to-[#010914] border border-white/[0.06] hover:border-white/20 transition-all duration-500 h-full backdrop-blur">
                  <div className="flex items-center justify-between mb-6">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center font-mono text-xs font-bold"
                      style={{
                        backgroundColor: `${s.color}18`,
                        color: s.color,
                        border: `1px solid ${s.color}40`,
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <s.icon className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" style={{ color: `${s.color}A0` }} />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-white mb-2 tracking-tight">{s.title}</h3>
                  <p className="text-[#5C7599] text-sm leading-relaxed">{s.desc}</p>
                  <div
                    className="absolute bottom-0 left-0 right-0 h-[2px] w-0 group-hover:w-full transition-all duration-700"
                    style={{ background: `linear-gradient(90deg, transparent, ${s.color}, transparent)` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-14 sm:mt-20">
            <Button
              onClick={onCta}
              className="btn-primary px-8 py-3 h-auto text-base group"
              data-testid="how-it-works-cta"
            >
              Start Your Journey
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* ─────────── FINAL CTA ─────────── */}
      <section className="relative py-16 sm:py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="keep-dark relative overflow-hidden rounded-[28px] border border-white/10 aga-grain"
            style={{
              background: 'radial-gradient(120% 100% at 0% 0%, rgba(0,229,255,0.15), transparent 55%), radial-gradient(120% 100% at 100% 100%, rgba(255,184,0,0.14), transparent 55%), linear-gradient(180deg, #041A3B 0%, #000B18 100%)',
            }}
          >
            <div className="relative z-[2] px-6 py-14 sm:px-14 sm:py-20 text-center">
              <span className="eyebrow">Ready When You Are</span>
              <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-semibold text-white mt-4 leading-[1.05]">
                Join a membership that <span className="text-luxe">works for you</span>.
              </h2>
              <p className="mt-5 text-[#A0B4CC] text-sm sm:text-base max-w-xl mx-auto">
                Register in minutes with real ID verification. Start earning, saving, and growing today.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Button
                  onClick={onCta}
                  className="btn-primary text-base px-8 py-3 h-auto group"
                  data-testid="cta-get-started-btn"
                >
                  Get Started Now
                  <ArrowUpRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
                <Link to="/marketplace">
                  <Button
                    variant="outline"
                    className="border-white/20 hover:border-gold text-white hover:bg-white/[0.03] text-base px-8 py-3 h-auto transition-all"
                  >
                    Explore Marketplace
                  </Button>
                </Link>
              </div>
              <p className="mt-8 text-[11px] tracking-[0.2em] text-[#5C7599] uppercase font-mono">
                No credit card to browse · Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
